/**
 * Settings data slice: the interface this feature's screens use + the live implementation over the US API
 * (core/http.ts → /api/v1/app/*). The fixture implementation (in-memory Lone Star tenant) is in ./fixture.ts, which is
 * loaded only in dev / VITE_FIXTURES builds. The price-book READ is core's useCatalog(); this slice owns the writes.
 *
 * Real routes today (read from api/src/modules):
 *   users & roles  P1 auth/routes.js   GET /users, POST /users/invite, PATCH /users/:id, POST /users/:id/{disable,enable,resend_invite},
 *                                      GET/POST /roles, PATCH /roles/:key
 *   voice + numbers P4 voice/routes.js GET/PUT /voice/settings (If-Match version), GET /phone-numbers, POST /phone-numbers/provision
 *   imports        P2 app_ui/routes.js POST /imports (Idempotency-Key; one per file), GET /imports/:id
 *   tenant & catalog P1 tenants/routes.js GET/PATCH /tenant (business_hours keyed by dow '0'–'6' ↔ mon…sun here),
 *                                      GET/POST /locations, PATCH /locations/:id, POST /locations/:id/archive,
 *                                      POST /catalog, PATCH /catalog/:id (If-Match), POST /catalog/:id/archive
 *   sales tax      P6 pay/routes/app.js GET /pay/tax-rates → {tax_rates, coverage}, POST /pay/tax-rates, POST /pay/tax-rates/:id/deactivate
 * Rolling out (no route yet; 404 → not_available): GET /audit?after&limit, GET/PUT /settings/notifications,
 *   GET /exports/:kind.csv, POST /billing/portal (P6).
 */
import { APP, ApiError, type Http } from '@app/core/http';
import { orRollingOut } from '@app/core/live/dto';
import type { CatalogItem, Cents, ISO, UUID } from '@app/core/types';

const enc = encodeURIComponent;
const list = <T>(r: unknown, key: string): T[] => (Array.isArray(r) ? r : ((r as Record<string, T[]>)[key] ?? (r as { items?: T[] }).items ?? [])) as T[];

// ─────────────────────────────────────────────────────────── tenant (P1 core.tenants)
export interface Tenant {
  id: UUID; name: string; slug: string; status: string; segment: string | null; timezone: string;
  settings: Record<string, unknown>;
  /** P1 tenants: the plan's default booking mode and whether the owner has chosen one (settings.booking_mode). */
  booking?: { mode_default: string; mode_chosen: boolean };
}
export interface TenantPatch { name?: string; segment?: string; default_timezone?: string; settings?: Record<string, unknown> }

// ─────────────────────────────────────────────────────────── locations (P1 core.locations)
export interface LocAddress { line1?: string; line2?: string; city?: string; state?: string; zip?: string }
export interface Loc {
  id: UUID; name: string; timezone: string; address: LocAddress; lat: number | null; lng: number | null;
  service_radius_m: number | null; phone_e164: string | null; is_default: boolean; archived_at: ISO | null;
}
export type LocInput = Partial<Pick<Loc, 'name' | 'timezone' | 'address' | 'service_radius_m' | 'phone_e164' | 'is_default'>>;

// ─────────────────────────────────────────────────────────── users & roles (P1 auth/users.js, exact columns)
export type UserStatus = 'active' | 'invited' | 'disabled';
export interface AppUser {
  id: UUID; email: string | null; phone_e164: string | null; first_name: string | null; last_name: string | null; role: string;
  extra_permissions: string[]; location_ids: UUID[] | null; status: UserStatus; last_login_at: ISO | null; created_at: ISO; tech_id: UUID | null;
}
export interface InviteInput { email?: string; phone?: string; first_name?: string; last_name?: string; role: string; extra_permissions?: string[]; location_ids?: UUID[] | null }
export interface UserPatch { role?: string; extra_permissions?: string[]; first_name?: string; last_name?: string; phone?: string | null; location_ids?: UUID[] | null }
export interface Role { key: string; name: string; permissions: string[]; system: boolean; users?: number }

// ─────────────────────────────────────────────────────────── voice + numbers (P4 voice/service.js effectiveSettings)
export type Span = [string, string];
export type WeekHours = Record<'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', Span[]>;
export interface TransferRule { e164: string; label: string; ring_sec: number; hours: 'business' | 'always' }
export interface VoiceSettings {
  timezone: string; business_hours: WeekHours; holidays: ({ date: string; label?: string } | string)[];
  ai_mode: 'always' | 'after_hours_only' | 'overflow'; after_hours_action: 'ai' | 'voicemail'; transfer_rules: TransferRule[];
  voice_id: string; recording_retention_days: number; ai_enabled: boolean; service_area_zips: string[];
  greeting: string | null; business_display_name: string | null; version: number;
}
export interface VoiceView { settings: VoiceSettings; saved?: boolean; disclosure?: { version: string; text: string } }
export interface PhoneNumber {
  id: UUID; e164: string; purpose: string; status: string; sms_enabled: boolean; forward_from_e164: string | null;
  forwarding_verified_at: ISO | null; forwarding_mode: string | null; created_at: ISO;
}
export interface ProvisionResult { status: string; number?: { e164: string } | null; dry_run?: boolean }

// ─────────────────────────────────────────────────────────── catalog writes (P1 tenants/catalog.js)
export type CatalogRow = CatalogItem & { version?: number };
export interface CatalogInput {
  name?: string; sku?: string | null; category?: string | null; kind?: CatalogItem['kind']; price_cents?: Cents;
  price_max_cents?: Cents | null; cost_cents?: Cents | null; default_minutes?: number | null; taxable?: boolean; active?: boolean;
}

// ─────────────────────────────────────────────────────────── imports (P2 app_ui, exact)
export type ImportKind = 'price_book' | 'customers' | 'techs';
export interface ImportError { row: number; field: string | null; code: string; message: string }
export interface ImportResult {
  id?: UUID; kind?: ImportKind; status: 'validated' | 'applied' | 'failed'; row_count: number;
  applied_count?: number; valid_count?: number; errors: ImportError[];
}
export interface ImportBody { kind: ImportKind; file_name?: string; mapping: Record<string, string>; rows: Record<string, string>[]; dry_run?: boolean }

// ─────────────────────────────────────────────────────────── audit, notifications, exports
export interface AuditRow { id: string; at: ISO; actor_name: string | null; actor_kind: string; action: string; entity: string; entity_id: string | null; summary: string | null }
export interface AuditPage { items: AuditRow[]; next: string | null }
export interface NotificationPrefs {
  staff: Record<string, { in_app: boolean; email: boolean }>;
  customer_sms: Record<string, boolean>;
  version: number;
}
// ─────────────────────────────────────────────────────────── sales tax (P6 pay.tax_rates; location_id null = tenant default)
export interface TaxRate {
  id: UUID; location_id: UUID | null; name: string; rate_bp: number; labor_taxable: boolean; parts_taxable: boolean; fees_taxable: boolean;
  active: boolean; effective_from: string | null; created_at: ISO;
}
export interface TaxCoverage { ready: boolean; missing_locations: { id: UUID; name: string }[] }
export interface TaxRates { tax_rates: TaxRate[]; coverage: TaxCoverage }
export type TaxRateInput = Pick<TaxRate, 'location_id' | 'name' | 'rate_bp' | 'labor_taxable' | 'parts_taxable' | 'fees_taxable'>;

export type ExportKind = 'customers' | 'vehicles' | 'jobs' | 'price_book' | 'payments';

export interface Api {
  tenant(): Promise<Tenant>;
  updateTenant(p: TenantPatch): Promise<Tenant>;
  locations(): Promise<Loc[]>;
  createLocation(v: LocInput, key: string): Promise<Loc>;
  updateLocation(id: string, v: LocInput): Promise<Loc>;
  archiveLocation(id: string): Promise<Loc>;

  users(): Promise<AppUser[]>;
  invite(v: InviteInput, key: string): Promise<{ user: AppUser; invite: { via: string; queued: boolean } }>;
  updateUser(id: string, p: UserPatch): Promise<AppUser>;
  disableUser(id: string, reason: string | null, key: string): Promise<AppUser>;
  enableUser(id: string, key: string): Promise<AppUser>;
  resendInvite(id: string, key: string): Promise<{ via: string; queued: boolean }>;
  roles(): Promise<Role[]>;
  createRole(v: { key: string; name: string; permissions: string[] }, key: string): Promise<Role>;
  updateRole(roleKey: string, p: { name?: string; permissions?: string[] }): Promise<Role>;

  voice(): Promise<VoiceView>;
  saveVoice(s: Omit<VoiceSettings, 'version'>, version: number): Promise<VoiceView>;
  numbers(): Promise<PhoneNumber[]>;
  provision(areaCode: string | null, key: string): Promise<ProvisionResult>;

  createItem(v: CatalogInput, key: string): Promise<CatalogRow>;
  updateItem(id: string, v: CatalogInput, version: number | undefined): Promise<CatalogRow>;
  archiveItem(id: string, key: string): Promise<CatalogRow>;

  runImport(body: ImportBody, key: string): Promise<ImportResult>;

  audit(q: { after?: string; limit?: number }): Promise<AuditPage>;
  notifications(): Promise<NotificationPrefs>;
  saveNotifications(p: Omit<NotificationPrefs, 'version'>, version: number): Promise<NotificationPrefs>;
  exportCsv(kind: ExportKind): Promise<Blob>;
  billingPortal(key: string): Promise<{ url: string }>;

  taxRates(): Promise<TaxRates>;
  createTaxRate(v: TaxRateInput, key: string): Promise<TaxRate>;
  deactivateTaxRate(id: string, key: string): Promise<TaxRate>;
}

type RawTenant = Omit<Tenant, 'timezone'> & { default_timezone?: string; timezone?: string };
/** P1 stores business_hours as {'0'..'6' (0 = Sunday): [{start,end}]}; the Hours screen edits {mon..sun: [[start,end]]}. */
const DOW = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
const weekIn = (bh: unknown): WeekHours | undefined => {
  if (!bh || typeof bh !== 'object') return undefined;
  const src = bh as Record<string, unknown>;
  if (!Object.keys(src).some((k) => /^[0-6]$/.test(k))) return src as WeekHours;
  const w = Object.fromEntries(DOW.map((d) => [d, []])) as unknown as WeekHours;
  for (const [k, v] of Object.entries(src)) {
    const d = DOW[Number(k)];
    if (d && Array.isArray(v)) w[d] = v.map((x: { start?: string; end?: string } | Span) => (Array.isArray(x) ? x : [String(x.start), String(x.end)] as Span));
  }
  return w;
};
const weekOut = (w: WeekHours) => Object.fromEntries(DOW.map((d, i) => [String(i), (w[d] ?? []).map(([start, end]) => ({ start, end }))]));
const tenantOf = (r: { tenant?: RawTenant } | RawTenant): Tenant => {
  const t = ('tenant' in r && r.tenant ? r.tenant : r) as RawTenant;
  const settings = { ...(t.settings ?? {}) };
  const week = weekIn(settings.business_hours);
  if (week) settings.business_hours = week;
  return { ...t, timezone: t.default_timezone ?? t.timezone ?? 'America/Chicago', settings };
};
/** P1's PATCH /tenant is strict: name, default_timezone and the known settings keys only (holidays are dates). */
const tenantPatchOut = (p: TenantPatch) => {
  const out: Record<string, unknown> = {};
  if (p.name !== undefined) out.name = p.name;
  if (p.default_timezone !== undefined) out.default_timezone = p.default_timezone;
  if (p.settings) {
    const { business_hours, holidays, hours: _summary, ...rest } = p.settings as Record<string, unknown>;
    const st: Record<string, unknown> = { ...rest };
    if (business_hours) st.business_hours = weekOut(business_hours as WeekHours);
    if (Array.isArray(holidays)) st.holidays = holidays.map((h) => (typeof h === 'string' ? h : String((h as { date: string }).date)));
    out.settings = st;
  }
  return out;
};
/** P1's catalog item schema is strict and has no price range. */
const itemOut = ({ price_max_cents: _max, ...v }: CatalogInput) => v;
const one = <T>(r: unknown, key: string): T => ((r as Record<string, T>)[key] ?? r) as T;

export const live = (http: Http): Api => ({
  tenant: () => http.get<RawTenant>(`${APP}/tenant`).then(tenantOf),
  updateTenant: (p) => http.patch<RawTenant>(`${APP}/tenant`, tenantPatchOut(p)).then(tenantOf),
  locations: () => http.get(`${APP}/locations`).then((r) => list<Loc>(r, 'locations')),
  createLocation: (v, key) => http.post(`${APP}/locations`, v, { idempotencyKey: key }).then((r) => one<Loc>(r, 'location')),
  updateLocation: (id, v) => http.patch(`${APP}/locations/${enc(id)}`, v).then((r) => one<Loc>(r, 'location')),
  archiveLocation: (id) => http.post(`${APP}/locations/${enc(id)}/archive`).then((r) => one<Loc>(r, 'location')),

  users: () => http.get(`${APP}/users`).then((r) => list<AppUser>(r, 'users')),
  invite: (v, key) => http.post(`${APP}/users/invite`, v, { idempotencyKey: key }),
  updateUser: (id, p) => http.patch(`${APP}/users/${enc(id)}`, p).then((r) => one<AppUser>(r, 'user')),
  disableUser: (id, reason, key) => http.post(`${APP}/users/${enc(id)}/disable`, { reason }, { idempotencyKey: key }).then((r) => one<AppUser>(r, 'user')),
  enableUser: (id, key) => http.post(`${APP}/users/${enc(id)}/enable`, {}, { idempotencyKey: key }).then((r) => one<AppUser>(r, 'user')),
  resendInvite: (id, key) => http.post(`${APP}/users/${enc(id)}/resend_invite`, {}, { idempotencyKey: key }).then((r) => one<{ via: string; queued: boolean }>(r, 'invite')),
  roles: () => http.get(`${APP}/roles`).then((r) => list<Role>(r, 'roles')),
  createRole: (v, key) => http.post(`${APP}/roles`, v, { idempotencyKey: key }).then((r) => one<Role>(r, 'role')),
  updateRole: (k, p) => http.patch(`${APP}/roles/${enc(k)}`, p).then((r) => one<Role>(r, 'role')),

  voice: () => http.get<VoiceView>(`${APP}/voice/settings`),
  saveVoice: (s, version) => http.put<VoiceView>(`${APP}/voice/settings`, { ...s, version: version || undefined }, { version: version || undefined }),
  numbers: () => http.get(`${APP}/phone-numbers`).then((r) => list<PhoneNumber>(r, 'numbers')),
  provision: (areaCode, key) => http.post(`${APP}/phone-numbers/provision`, { ...(areaCode ? { area_code: areaCode } : {}), purpose: 'main' }, { idempotencyKey: key }),

  createItem: (v, key) => http.post(`${APP}/catalog`, itemOut(v), { idempotencyKey: key }).then((r) => one<CatalogRow>(r, 'item')),
  updateItem: (id, v, version) => http.patch(`${APP}/catalog/${enc(id)}`, itemOut(v), { version }).then((r) => one<CatalogRow>(r, 'item')),
  archiveItem: (id, key) => http.post(`${APP}/catalog/${enc(id)}/archive`, {}, { idempotencyKey: key }).then((r) => one<CatalogRow>(r, 'item')),

  runImport: (body, key) => http.post(`${APP}/imports`, body, { idempotencyKey: key }).then((r) => one<ImportResult>(r, 'import')),

  audit: (q) => http.get<AuditPage>(`${APP}/audit`, { query: { after: q.after, limit: q.limit ?? 50 } }).catch(orRollingOut('audit_log')),
  notifications: () => http.get(`${APP}/settings/notifications`).then((r) => one<NotificationPrefs>(r, 'notifications'), orRollingOut('notification_settings')),
  saveNotifications: (p, version) => http.put(`${APP}/settings/notifications`, p, { version }).then((r) => one<NotificationPrefs>(r, 'notifications'), orRollingOut('notification_settings')),
  exportCsv: async (kind) => {
    const res = await http.get<Response>(`${APP}/exports/${enc(kind)}.csv`, { raw: true });
    if (res.status === 404 || res.status === 405) return orRollingOut('csv_export')(new ApiError(res.status, 'not_found', null));
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    return res.blob();
  },
  billingPortal: (key) => http.post<{ url: string }>(`${APP}/billing/portal`, {}, { idempotencyKey: key }).catch(orRollingOut('billing_portal')),

  taxRates: () => http.get<TaxRates>(`${APP}/pay/tax-rates`).then((r) => ({ tax_rates: r.tax_rates ?? [], coverage: r.coverage ?? { ready: false, missing_locations: [] } })),
  createTaxRate: (v, key) => http.post(`${APP}/pay/tax-rates`, v, { idempotencyKey: key }).then((r) => one<TaxRate>(r, 'tax_rate')),
  deactivateTaxRate: (id, key) => http.post(`${APP}/pay/tax-rates/${enc(id)}/deactivate`, {}, { idempotencyKey: key }).then((r) => one<TaxRate>(r, 'tax_rate')),
});
